import { useEffect, useRef, useState } from "react";
import {
  type ViewModel,
  complaintViewModelToServerModel,
} from "./Vm/ViewModel";
import getViewModelNewValues from "./Vm/ViewModelNewValues";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { httpPost } from "../../Common/Http/httpPost";
import getClassMetadata from "../../Common/classMetadataService";
import LoadingContentWrapper from "../../Common/Loading/LoadingContentWrapper";
import TextInputFormRow from "../../Common/EditForm/TextInput/TextInputFormRow";
import getTextMetadata from "../../Common/EditForm/ClassMetadata/getTextMetadata";
import TextAreaSection from "../../Common/EditForm/TextArea/TextAreaSection";
import getTextAreaMetadata from "../../Common/EditForm/ClassMetadata/getTextAreaMetadata";
import { AttachmentUpload } from "./controls/AttachmentUpload";
import FormButtons from "../../Common/EditForm/formButtons";
import SaveError from "../../Common/EditForm/SaveError";
import { ConfirmDialog } from "../../Common/Dialog/ConfirmDialog";
import httpPostNoBody from "../../Common/Http/httpPostNoBody";
import SaveSuccess from "./controls/SaveSuccess";
import ProductReturnRequestItemDisplayFragment from "./controls/ProductReturnRequestItemDisplayFragment";
import { AlertDialog } from "../../Common/Dialog/AlertDialog";
import BusinessDocumentSelectorFormRow, {
  type BusinessDocumentSelectorHandle,
} from "../../BusinessDocuments/BusinessDocumentSelector/BusinessDocumentSelectorFormRow";
import selectorSection from "../../BusinessDocuments/BusinessDocumentSelector/selectorSection";
import "../../styles/fakeDisabled.scss";
import ProductReturnRequestItemForm from "./ProductReturnRequestItemForm";
import type businessDocumentVM from "../../BusinessDocuments/BusinessDocumentSelector/businessDocumentVM";
import type { ProductReturnRequestItemViewModel } from "./Vm/ProductReturnRequestItemViewModel";
import type { ProductReturnRequestCreatedVM } from "./Vm/ProductReturnRequestCreatedVM";
import type { ProductReturnRequestServerVM } from "./Vm/ProductReturnRequestServerVM";

export default function ProductReturnRequestForm(props: {
  navigate: (url: string) => void;
  backUrl?: string;
  onOk?: (id: number, name: string) => void;
  onCancel?: () => void;
}) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaveError, setIsSaveError] = useState<boolean>(false);
  const [isSaveSuccessful, setIsSaveSuccessful] = useState<boolean>(false);
  const [createdProductReturnRequest, setCreatedProductReturnRequest] =
    useState<ProductReturnRequestCreatedVM | null>(null);
  // biome-ignore lint/suspicious/noExplicitAny: metadata is server-dependent and can contain various properties, so it's not feasible to strongly type it
  const [metadata, setMetadata] = useState<{ [key: string]: any }>({});

  const [isItemFormShown, setIsItemFormShown] = useState<boolean>(false);
  const [editedItem, setEditedItem] =
    useState<ProductReturnRequestItemViewModel | null>(null);
  const [editedItemIndex, setEditedItemIndex] = useState<number>(0);

  const [isDocumentSelected, setIsDocumentSelected] = useState<boolean>(false);

  const methods = useForm<ViewModel>({
    mode: "onTouched",
    defaultValues: getViewModelNewValues(),
  });

  const { handleSubmit, formState, control, getValues, reset } = methods;

  const businessDocumentSelectorRef =
    useRef<BusinessDocumentSelectorHandle>(null);

  const itemsMethods = useFieldArray<ViewModel>({
    control,
    name: "items",
    rules: {},
  });

  /** True when there are any lines in this complaint */
  const areThereAnyItems =
    (itemsMethods.fields as ProductReturnRequestItemViewModel[]).length > 0;

  const { isSubmitting } = formState;

  const onSubmit = async (values: ViewModel) => {
    if (values.items.length === 0) {
      await AlertDialog(
        "Brak pozycji",
        "Musisz wprowadzić co najmniej jedną pozycję zgłoszenia.",
      );
      return;
    }

    // check whether there are multiple items referring to the same document item (lineNumber), if so, show an alert and do not submit the form

    const lineNumbers = values.items
      .map((x) => x.documentItem?.lineNumber)
      .filter((x) => x !== undefined);
    const hasDuplicates = new Set(lineNumbers).size !== lineNumbers.length;

    if (hasDuplicates) {
      await AlertDialog(
        "Powtarzające się pozycje dokumentu",
        "We wniosku dodałeś pozycje, w których odwołujesz się do tych samych pozycji dokumentu.\r\n" +
          "Każda pozycja wniosku musi odnosić się do innej pozycji dokumentu.\r\n" +
          "Połącz powtarzające się pozycje lub usuń je.\r\n",
      );
      return;
    }

    // confirm submission

    const confirm = await ConfirmDialog(
      "Potwierdzenie wysyłania formularza",
      "Czy chcesz zakończyć wypełnianie formularza wniosku o zwrot towaru i wysłać go?\r\nWysłanego zgłoszenia nie można wycofać ani edytować.",
    );

    if (confirm === false) return;

    // let vmToSave = Object.assign({}, viewModel, values); // if values contain all the necessary data, this step will be unnecessary
    const data = complaintViewModelToServerModel(values);

    try {
      const created = await httpPost<
        ProductReturnRequestServerVM,
        ProductReturnRequestCreatedVM
      >(
        "/api/ProductReturns/ProductReturnRequest",
        data,
      );
      setCreatedProductReturnRequest(created);
      setIsSaveSuccessful(true);
    } catch {
      setIsSaveError(true);
    }

    return;
  };

  const onInitialize = async () => {
    // load form values
    // fetch existing data for the form
    // load data for selects, etc.
    // this method is async so we can use await here

    const metadata_response = await getClassMetadata("ProductReturnRequestVM");
    setMetadata(metadata_response);

    reset(getViewModelNewValues());

    setIsLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: because onInitialize is only meant to be called once on component mount, and it does not have any dependencies
  useEffect(() => {
    onInitialize();
  }, []);

  const onCancel = async () => {
    const r = await ConfirmDialog(
      "Anulowanie wysyłania",
      "Czy na pewno chcesz porzucić ten formularz?\r\nWybierz OK aby porzucić formularz.",
    );
    if (r === true) {
      const attachments = getValues("attachments");
      for (const attachement of attachments) {
        try {
          const fileId = attachement.id;
          await httpPostNoBody(
            `/api/Attachment/AttachmentUpload/removeUploadedFile/${fileId}`,
          );
        } catch {
          // silently ignore a failure to remove an attachment, it will be deleted anyway.
        }
      }
      props.navigate("./../index");
    }
  };

  const onSaveSuccess = async () => {
    props.navigate("./../index");
  };

  const onRemoveItem = async (index: number) => {
    const r = await ConfirmDialog(
      "Usunięcie pozycji",
      `Czy na usunąć pozycję ${index + 1} ze zgłoszenia?`,
    );
    if (r === true) {
      itemsMethods.remove(index);
    }
  };

  const onEditItem = async (index: number) => {
    const item = getValues().items[index];
    setEditedItem(item);
    setEditedItemIndex(index);
    setIsItemFormShown(true);
  };

  const onNewItem = async () => {
    setEditedItem(null);
    setIsItemFormShown(true);
  };

  const onItemSave = async (values: ProductReturnRequestItemViewModel) => {
    if (editedItem !== null) {
      // this was an edit
      itemsMethods.update(editedItemIndex, values);
    } else {
      // biome-ignore lint/style/noNonNullAssertion: this was a new item and values cannot be null
      itemsMethods.append(values!);
    }
    setIsItemFormShown(false);
  };

  return (
    <>
      {!isItemFormShown && (
        <div className="content-wrapper">
          <h1 className="border-bottom pb-2 mt-4 mb-5">
            Formularz wniosku o zwrot towaru
          </h1>
          <LoadingContentWrapper isLoading={isLoading}>
            {!isLoading && !isSaveError && !isSaveSuccessful && (
              <FormProvider {...methods}>
                <form noValidate>
                  <TextInputFormRow
                    {...getTextMetadata<ViewModel>(
                      "customerRepName",
                      metadata["customerRepName"],
                    )}
                    label="Imię i nazwisko *"
                    longDescription="Imię i nazwisko osoby składającej wniosek (wymagane)."
                    control={control}
                    inlineDescription={undefined}
                  />

                  <TextInputFormRow
                    {...getTextMetadata<ViewModel>(
                      "customerRepEMail",
                      metadata["customerRepEMail"],
                    )}
                    label="Adres e-mail *"
                    longDescription="Adres e-mail osoby składającej wniosek (wymagane)."
                    control={control}
                    inlineDescription={undefined}
                  />

                  <TextInputFormRow
                    {...getTextMetadata<ViewModel>(
                      "customerRepPhone",
                      metadata["customerRepPhone"],
                    )}
                    label="Numer telefonu"
                    longDescription="Numer telefonu osoby składającej wniosek (niewymagane)."
                    control={control}
                    inlineDescription={undefined}
                    className="-app-medium-control"
                  />

                  <BusinessDocumentSelectorFormRow
                    label="Numer dokumentu sprzedaży *"
                    name="salesDocument"
                    id="salesDocument"
                    control={control}
                    longDescription="Numer dokumentu WZ, którego dotyczą towary do zwrotu (wymagane)."
                    required={true}
                    selectorSectionId={selectorSection.returnRequests}
                    ref={businessDocumentSelectorRef}
                    locked={areThereAnyItems}
                    onDocumentChanged={(
                      businessDocument: businessDocumentVM,
                    ) => {
                      setIsDocumentSelected(!!businessDocument);
                    }}
                  />

                  {areThereAnyItems && (
                    <div className="row mb-1">
                      <div className="col-sm-8 offset-sm-4">
                        <div
                          className="alert alert-primary d-flex align-items-center"
                          role="alert"
                        >
                          <div className="flex-shrink-0 ms-3 me-4">
                            <i className="bi bi-lightbulb"></i>
                          </div>
                          <div>
                            Nie możesz zmienić dokumentu sprzedaży, ponieważ
                            dodałeś już pozycję wniosku o zwrot towaru odnoszącą
                            się do niego.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <hr />
                  <h2
                    className="mb-3"
                    title="Poniżej wprowadź pozycje dokumentu, których dotyczy wniosek."
                  >
                    Pozycje
                    <sup>
                      <i className="bi bi-question-circle -app-h2-section-label-help"></i>
                    </sup>
                  </h2>

                  <ProductReturnRequestItemDisplayFragment
                    items={
                      itemsMethods.fields as ProductReturnRequestItemViewModel[]
                    }
                    onRemoveItem={onRemoveItem}
                    onEditItem={onEditItem}
                    onNewItem={onNewItem}
                    isNewItemEnabled={isDocumentSelected}
                    onOpenDocumentSelector={() =>
                      businessDocumentSelectorRef.current?.openSelector()
                    }
                  ></ProductReturnRequestItemDisplayFragment>

                  <hr />
                  <h2
                    className="mb-3"
                    title="Poniżej możesz dodać załączniki i podać ich opis."
                  >
                    Załączniki
                    <sup>
                      <i className="bi bi-question-circle -app-h2-section-label-help"></i>
                    </sup>
                  </h2>

                  <AttachmentUpload parent="attachments"></AttachmentUpload>

                  <hr />

                                    <TextAreaSection
                    {...getTextAreaMetadata<ViewModel>(
                      "comments",
                      metadata["comments"],
                    )}
                    rows={5}
                    label="Uwagi"
                    longDescription="Możesz dodać uwagi do całego wniosku o zwrot towaru."
                    control={control}
                    inlineDescription={undefined}
                    required={false}
                  />

                  <hr />

                  <div className="row pt-2">
                    <div className="offset-sm-4 col-sm-8">
                      <FormButtons
                        onSave={(e) => handleSubmit(onSubmit)(e)}
                        onCancel={() => onCancel()}
                        isSubmitting={isSubmitting}
                      ></FormButtons>
                    </div>
                  </div>
                </form>
              </FormProvider>
            )}
            {isSaveError && (
              <SaveError
                navigate={props.navigate}
                backUrl="./../index"
              ></SaveError>
            )}
            {isSaveSuccessful && createdProductReturnRequest && (
              <SaveSuccess
                done={() => onSaveSuccess()}
                productReturnRequest={createdProductReturnRequest}
              />
            )}
          </LoadingContentWrapper>
        </div>
      )}

      {isItemFormShown && (
        <ProductReturnRequestItemForm
          values={editedItem}
          onOk={(values: ProductReturnRequestItemViewModel) =>
            onItemSave(values)
          }
          onCancel={() => {
            setIsItemFormShown(false);
          }}
          businessDocumentType={
            getValues("salesDocument")?.businessDocumentType || 1
          }
          documentNumber={getValues("salesDocument")?.documentNumber || ""}
        ></ProductReturnRequestItemForm>
      )}
    </>
  );
}
